import { RoleProvider } from "context/RoleContext";
import { globalVariables } from "helper/globalVariables";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ProtectedRoutes from "routes/protectedRoutes";
import PublicRoutes from "routes/publicRoutes";
import { PopUpMessages } from "shared/Components/popUpMessages";
import {
  setAuthToken,
  setOriginalRoleAction,
  setRoleAction,
  setUser,
} from "redux/features/authSlice/AuthSlice";
import {
  setHostDetails,
  setTrialDetails,
} from "redux/features/hostSlice/addTrialSlice/addTrialSlice";
import { setMemberDetails } from "redux/features/memberSlice/memberSlice";
import * as Sentry from "@sentry/react";

function App() {
  const dispatch = useDispatch();
  const {
    auth: {
      authReducer: { token, loginTime, user },
    },
  } = useSelector((state) => state) || {};
  const {
    toast: { show, message, callBack },
  } = useSelector((state) => state.global) || {};
  const { memberReducer } = useSelector((state) => state.auth) || {};

  const modalRef = useRef(null);

  const timeoutRef = useRef(null);

  const onLogout = () => {
    // Clear Sentry user context on logout
    Sentry.setUser(null);

    dispatch(setUser(null));
    dispatch(setAuthToken(""));
    dispatch(setMemberDetails(null));
    dispatch(setHostDetails(null));
    dispatch(setTrialDetails(null));
    dispatch(setRoleAction(""));
    dispatch(setOriginalRoleAction(""));
  };

  useEffect(() => {
    if (token) {
      const now = Date.now();
      const maxSessionDuration = 12 * 60 * 60 * 1000;
      const remaining = maxSessionDuration - (now - loginTime);

      if (remaining <= 0) {
        onLogout();
        return;
      }

      timeoutRef.current = setTimeout(() => {
        onLogout();
      }, remaining);

      return () => clearTimeout(timeoutRef.current);
    } else {
      clearTimeout(timeoutRef.current);
    }
  }, [token, loginTime]);

  useEffect(() => {
    if (show) {
      modalRef.current.openModal();
    }
  }, [show]);

  // Set Sentry user context when user is logged in
  useEffect(() => {
    if (user || memberReducer?.memberDetails) {
      const userEmail = user?.email || memberReducer?.memberDetails?.email;
      const userRole = user?.role || memberReducer?.memberDetails?.role;
      const authId = user?.sub || memberReducer?.memberDetails?.authId;

      Sentry.setUser({
        email: userEmail,
        id: authId,
        role: Array.isArray(userRole) ? userRole.join(", ") : userRole,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user, memberReducer]);

  return token ? (
    <RoleProvider>
      <ProtectedRoutes />
      <PopUpMessages
        title={globalVariables.error}
        type="pending"
        desc={message}
        onCloseModal={callBack}
        ref={modalRef}
        preventClickOnOutside={true}
      />
    </RoleProvider>
  ) : (
    <PublicRoutes />
  );
}

export default App;
